import { useEffect, useState } from 'react';
import { Camera, MoreHorizontal, Plus } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useInstanceSnapshots,
  useCreateSnapshot,
  useRevertSnapshot,
  useDeleteSnapshot,
  type Instance,
  type InstanceSnapshot,
} from '@/api/opennebula';

const snapSizeGB = (mb?: string | number) => {
  const n = Number(mb);
  return Number.isFinite(n) && n > 0 ? Math.round((n / 1024) * 10) / 10 : 0;
};
const snapDate = (epoch?: string | number) => {
  const ms = Number(epoch) * 1000;
  return Number.isFinite(ms) && ms > 0 ? new Date(ms).toLocaleString() : '—';
};

export function SnapshotTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { show } = useToast();
  const { data: snapshots = [], isLoading } = useInstanceSnapshots(serviceId, instance.id);
  const [createOpen, setCreateOpen] = useState(false);
  const [revert, setRevert] = useState<InstanceSnapshot | null>(null);
  const [del, setDel] = useState<InstanceSnapshot | null>(null);

  // Reverting a disk snapshot rewrites the live disk, so the backend only
  // accepts it while the instance is stopped — a running (or terminated)
  // instance is rejected. Match that by gating the action on instance state.
  const canRevert = instance.status !== 'POWERED_ON' && instance.status !== 'TERMINATED';

  const revertM = useRevertSnapshot(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Revert requested' });
      setRevert(null);
    },
    onError: () => show({ tone: 'danger', title: 'Revert failed' }),
  });
  const delM = useDeleteSnapshot(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Snapshot deleted' });
      setDel(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<InstanceSnapshot, unknown>[] = [
    {
      id: 'name',
      header: 'Snapshot',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || `snapshot-${row.original.id}`}</span>,
    },
    {
      id: 'id',
      header: 'Snapshot ID',
      cell: ({ row }) => <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.id}</code>,
    },
    {
      id: 'disk',
      header: 'Disk',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>disk-{row.original.disk_id ?? '0'}</span>,
    },
    {
      id: 'size',
      header: 'Size',
      cell: ({ row }) => <span>{snapSizeGB(row.original.size)} GB</span>,
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{snapDate(row.original.date)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Snapshot actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!canRevert}
                onSelect={() => canRevert && setRevert(row.original)}
              >
                Revert to snapshot
              </DropdownMenuItem>
              <DropdownMenuItem tone="danger" onSelect={() => setDel(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Snapshots"
      description="Point-in-time disk snapshots you can revert to."
      actions={
        <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
          Take snapshot
        </Button>
      }
    >
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : (
        // Empty placeholder renders inside the table's own bordered frame
        // (via `emptyState`), so the zone keeps the same frame whether empty
        // or full — no layout jump between states.
        <DataTable
          columns={columns}
          data={snapshots}
          getRowId={(s, i) => String(s.id ?? i)}
          emptyState={
            <EmptyState
              icon={<Camera size={28} />}
              title="No snapshots"
              description="Take a snapshot to capture the current disk state."
              actions={
                <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
                  Take snapshot
                </Button>
              }
            />
          }
        />
      )}

      <CreateSnapshotDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        serviceId={serviceId}
        instance={instance}
        snapshotCount={snapshots.length}
      />

      <ConfirmDialog
        open={!!revert}
        onOpenChange={(o) => !o && setRevert(null)}
        title="Revert to snapshot?"
        description="The instance disk will be rolled back to this snapshot. Changes since then are lost."
        confirmLabel="Revert"
        destructive
        loading={revertM.isPending}
        onConfirm={() => revert && revertM.mutate(revert.id)}
      />
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete snapshot?"
        description="This permanently removes the snapshot."
        confirmLabel="Delete"
        destructive
        loading={delM.isPending}
        onConfirm={() => del && delM.mutate(del.id)}
      />
    </Section>
  );
}

function CreateSnapshotDialog({
  open,
  onOpenChange,
  serviceId,
  instance,
  snapshotCount,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  instance: Instance;
  snapshotCount: number;
}) {
  const { show } = useToast();
  const disks = instance.metadata?.disks ?? [];
  const firstDiskId = String(disks[0]?.disk_id ?? '0');

  // Legacy auto-generates the snapshot name from the instance owner, the
  // selected disk and the running snapshot count, and regenerates it whenever
  // the disk changes. Users may still override the generated value.
  const genName = (diskId: string) =>
    `instance-${instance.uid ?? '0'}-disk-${diskId}-snapshot-${snapshotCount}`;

  const [diskId, setDiskId] = useState(firstDiskId);
  const [name, setName] = useState(() => genName(firstDiskId));

  // Reset the form to a freshly generated name each time the dialog opens.
  useEffect(() => {
    if (open) {
      setDiskId(firstDiskId);
      setName(genName(firstDiskId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDiskChange = (value: string) => {
    setDiskId(value);
    setName(genName(value));
  };

  const create = useCreateSnapshot(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Snapshot requested' });
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Snapshot failed' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Take snapshot</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p
            style={{
              margin: '0 0 var(--vn-space-2)',
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-muted)',
            }}
          >
            Snapshots can add to your instance costs. Snapshots must be created individually for each
            instance disk.
          </p>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Name" required>
              {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="Snapshot name" />}
            </Field>
            <Field label="Disk">
              {(p) => (
                <Select value={diskId} onValueChange={handleDiskChange}>
                  <SelectTrigger {...p} style={{ width: '100%' }}>
                    <SelectValue placeholder="Select a disk" />
                  </SelectTrigger>
                  <SelectContent>
                    {disks.map((d) => (
                      <SelectItem key={d.disk_id} value={String(d.disk_id)}>
                        disk-{d.disk_id} {d.image ? `(${d.image})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate({ name: name.trim(), disk_id: diskId })}
          >
            Take snapshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
